import React, { FC, ReactText, useMemo, useRef } from 'react';
import { Alert, Dropdown, MenuProps } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { PaginationConfig } from 'antd/lib/pagination';
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  NewClosedProjectsReportResponse,
  OptionProps,
  ReportKey,
  ReportsFilters,
} from '@models/interfaces';
import { CustomTable } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import type { ReportsSummaryMetric } from '../../models';
import {
  FULL_FILTER_CAPTION,
  getNewClosedProjectsColumns,
  getNewClosedProjectsReportRowKey,
  hasNewClosedProjectsSeriesData,
  NEW_CLOSED_PROJECTS_AREA_SERIES,
  NEW_CLOSED_PROJECTS_CHART_COLORS,
} from '../../models';
import { useReportExport } from '../../models/use-report-export';
import { ReportChartCard } from '../report-chart-card';
import { ReportsSummary } from '../reports-summary';

import './styles.scss';
import themeColors from 'styles/theme/colors.module.scss';

interface NewClosedProjectsReportProps {
  data?: NewClosedProjectsReportResponse;
  filters: ReportsFilters;
  onTableChange: (
    pagination: PaginationConfig,
    tableFilters: Record<string, ReactText[] | null>,
    sorter: SorterResult<Store> | SorterResult<Store>[],
    extra: TableCurrentDataSource<Store>,
  ) => void;
  sortByOptions: OptionProps[];
}

const CHART_GRID_STROKE = themeColors.chartGrid;
const CHART_AXIS_STROKE = themeColors.fontColor;

export const NewClosedProjectsReport: FC<NewClosedProjectsReportProps> = ({
  data,
  filters,
  onTableChange,
  sortByOptions,
}: NewClosedProjectsReportProps) => {
  const chartsRef = useRef<HTMLDivElement>(null);
  const rows = data?.table?.items ?? [];
  const total = data?.table?.total ?? 0;
  const series = data?.charts?.series ?? [];
  const hasSeriesData = hasNewClosedProjectsSeriesData(series);
  const columns = useMemo(
    () => getNewClosedProjectsColumns(filters, sortByOptions),
    [filters, sortByOptions],
  );
  const metrics: ReportsSummaryMetric[] = [
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Total New',
      value: data?.summary?.newProjects ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Total Closed',
      value: data?.summary?.closedProjects ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Net Change',
      value: data?.summary?.netChange ?? 0,
    },
  ];

  const { exporting, exportError, onExportExcel, onExportPdf } =
    useReportExport({
      columns,
      metrics,
      rows,
      chartsRef,
      reportTitle: 'New Closed Projects',
      scope: {
        page: filters.page ?? 1,
        size: filters.size ?? 25,
        total,
        filters,
      },
    });

  const exportMenu: MenuProps = {
    items: [
      { key: 'excel', label: 'Export as Excel', onClick: onExportExcel },
      { key: 'pdf', label: 'Export as PDF', onClick: onExportPdf },
    ],
  };

  return (
    <div className="prov-new-closed-projects-report">
      <div className="prov-new-closed-projects-report__toolbar">
        <p className="prov-new-closed-projects-report__description">
          Counts projects opened (&quot;new,&quot; by the date the project was
          created) versus projects finished (&quot;closed,&quot; by the Site Acq
          Complete actual date) within a period, bucketed by month or quarter.
          Projects currently Authorized, on Hold or Suspended are not counted as
          new; they count again once back to Active.
        </p>
        <Dropdown
          disabled={!rows.length || exporting}
          menu={exportMenu}
          trigger={['click']}
        >
          <PrimaryButton
            className="prov-new-closed-projects-report__export"
            title={exporting ? 'Exporting…' : 'Export current page ▾'}
          />
        </Dropdown>
      </div>

      {exportError && <Alert type="error" message={exportError} />}
      <ReportsSummary metrics={metrics} />

      <div className="prov-new-closed-projects-report__charts" ref={chartsRef}>
        <ReportChartCard
          hasData={hasSeriesData}
          title="New vs Closed Projects Over Time"
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={series}
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="period" stroke={CHART_AXIS_STROKE} />
              <YAxis allowDecimals={false} stroke={CHART_AXIS_STROKE} />
              <Tooltip />
              <Legend />
              {NEW_CLOSED_PROJECTS_AREA_SERIES.map(
                ({ dataKey, fill, name }) => (
                  <Area
                    dataKey={dataKey}
                    fill={fill}
                    fillOpacity={0.35}
                    key={dataKey}
                    name={name}
                    stackId="projects"
                    stroke={fill}
                    type="monotone"
                  />
                ),
              )}
            </AreaChart>
          </ResponsiveContainer>
        </ReportChartCard>

        <ReportChartCard
          hasData={hasSeriesData}
          title="New, Closed, and Cumulative Net Change"
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={series}
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="period" stroke={CHART_AXIS_STROKE} />
              <YAxis
                allowDecimals={false}
                stroke={CHART_AXIS_STROKE}
                yAxisId="counts"
              />
              <YAxis
                allowDecimals={false}
                orientation="right"
                stroke={NEW_CLOSED_PROJECTS_CHART_COLORS.cumulative}
                yAxisId="cumulative"
              />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="newCount"
                fill={NEW_CLOSED_PROJECTS_CHART_COLORS.new}
                name="New Projects"
                yAxisId="counts"
              />
              <Bar
                dataKey="closedCount"
                fill={NEW_CLOSED_PROJECTS_CHART_COLORS.closed}
                name="Closed Projects"
                yAxisId="counts"
              />
              <Line
                dataKey="cumulativeNetChange"
                dot={false}
                name="Cumulative Net Change"
                stroke={NEW_CLOSED_PROJECTS_CHART_COLORS.cumulative}
                strokeWidth={2}
                type="monotone"
                yAxisId="cumulative"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ReportChartCard>
      </div>

      <CustomTable
        module="reports"
        subModule={ReportKey.NewClosedProjects}
        className="prov-new-closed-projects-report__table"
        columns={columns}
        dataSource={rows}
        onChange={onTableChange}
        pagination={{
          current: filters.page,
          pageSize: filters.size,
          total,
        }}
        rowKey={getNewClosedProjectsReportRowKey}
        scroll={{ x: 2600, y: 'auto' }}
      />
    </div>
  );
};
